import { isRulerLineType, RulerLineType, SingleLineType, WallConnectionEnd, WallConnectionStart, WallType } from "../../types/floorplannerTypes";
import * as THREE from 'three';
import { floorplannerStore } from '../../store/floorplannerStore';
import { editorStore } from "../../store/editorStore";
import { applyAlignmentAfterSnap } from "./applyAlignmentAfterSnap";
import { applyWallPivot } from "./applyWallPivot";
import { runInAction } from "mobx";
import { renderStore } from "../../store/renderStore";
import { transformArcControlPoint } from "../../utils/arcUtils";

const snapAngle = (angle: number, snapInterval: number) => {
    return Math.round(angle / snapInterval) * snapInterval;
};

/**
 * Drag the end handle of the object, it will move the end of the object maintaining connections and updates their positions
 *
 * SNAPPING BEHAVIOR:
 * - Snapping preserves wall length for endpoint connections
 * - Edge connections are recorded at wall edges, not centers
 * - Uses removeEdgeConnection/addEdgeConnection for endpoint connections
 * - Mid-point connections use center-based snapping
 * - The system intelligently chooses connection type based on snap location
 * - Behavior mirrors dragStartHandle.ts
 *
 * @param source The object to drag
 * @param endOffsetX The end x offset of the wall
 * @param endOffsetY The end y offset of the wall
 * @param newX The new x position of the wall
 * @param newY The new y position of the wall
 * @param dragEndFreeToConnect If the end is free to snap
 * @param delta The delta vector
 * @param suppressSnapping When true, skips connection operations for deferred mode
 * @returns boolean If the wall was snapped
 */
export const dragEndHandle = (
    source: WallType | SingleLineType | RulerLineType,
    endOffsetX: number,
    endOffsetY: number,
    newX: number,
    newY: number,
    dragEndFreeToConnect: boolean,
    delta: THREE.Vector2,
    suppressSnapping: boolean = false,
): boolean => {
    const object = floorplannerStore.findObjectId(source.id);
    if (!object) {
        return false;
    }
    const dragState = floorplannerStore.wallDragState.get(object.id);
    const snapTolerance = editorStore.interactionSnappingTolerance();
    const angleSnapTolerance = editorStore.interactionSnappingTolerance(0.2);
    let snapped = false;
    let newDelta = new THREE.Vector2(newX - endOffsetX, newY - endOffsetY).sub(
        object.end,
    );
    const newEndPosition = object.end.clone().add(newDelta);
    let snappedWallAngle: number | undefined = undefined;

    // SNAPPING PRIORITY ORDER:
    // 1. Wall edge corners (for wall-to-wall connections) - HIGHEST PRIORITY
    // 2. 45-degree angle snapping (when within 20 degrees of snap angle)
    // 3. Alignment lines - LOWEST PRIORITY

    // Skip hysteresis during drag preview - calculate snap position deterministically based on mouse position
    // Only use hysteresis on final drop (when suppressSnapping=false)
    const maintainAlignmentLock = () => {
        const lock = dragState?.alignmentLocks?.end;
        if (!lock) {
            return;
        }
        const holdTolerance = snapTolerance;
        const releaseTolerance = snapTolerance * 1.5;
        const distance = newEndPosition.distanceTo(lock);
        if (distance <= holdTolerance) {
            newEndPosition.copy(lock);
        } else if (distance >= releaseTolerance && dragState?.alignmentLocks) {
            dragState.alignmentLocks.end = undefined;
        }
    };

    if (editorStore.isShiftPressed && dragState?.alignmentLocks?.end) {
        dragState.alignmentLocks.end = undefined;
    } else if (!suppressSnapping) {
        // Only apply hysteresis on final drop, not during drag preview
        maintainAlignmentLock();
    }

    // Handle wall connections and snapping
    let edgeSnapApplied = false;
    let pendingPositions: { start: THREE.Vector2; end: THREE.Vector2 } | null = null;
    const anchorStart = dragState?.fixedEndpoints?.start ?? object.start;
    let pendingStartPosition = anchorStart.clone();

    // PRIORITY 1: Wall edge corner snapping (highest priority)
    // Check for edge snap BEFORE angle snapping - wall connections take priority
    if (!suppressSnapping && dragEndFreeToConnect && !isRulerLineType(object)) {
        // Edge snapping is the default mode unless ALT/OPTION key is pressed
        // Also check that Shift is not pressed (Shift disables all snapping)
        const shouldUseEdgeSnapping = !editorStore.isAltPressed && !editorStore.isShiftPressed;
        if (shouldUseEdgeSnapping) {
            // Edge mode: use edge-based snapping (default) - NO fallback to center
            const snapCandidate = floorplannerStore.getEdgeSnappingPositionForDrag(object.id, "end", newEndPosition);
            const snapPoint = snapCandidate?.snappingPosition;
            if (snapPoint && newEndPosition.distanceTo(snapPoint) < snapTolerance) {
                // Calculate the snap delta
                const snapDelta = new THREE.Vector2().subVectors(snapPoint, newEndPosition);
                // Only move the dragged endpoint (end), keep the other end fixed
                newEndPosition.add(snapDelta);
                // pendingStartPosition stays at anchorStart (already initialized above)
                pendingPositions = {
                    start: pendingStartPosition.clone(),
                    end: newEndPosition.clone(),
                };
                snapped = true;
                edgeSnapApplied = true;
                floorplannerStore.storeEdgeSnappingPreview(object.id, "end", snapCandidate);
            } else {
                floorplannerStore.storeEdgeSnappingPreview(object.id, "end", null);
            }
            // IMPORTANT: Do NOT fall back to center snapping in edge mode
            // If no edge snap found, simply don't snap (user can press ALT for center mode)
        } else {
            floorplannerStore.storeEdgeSnappingPreview(object.id, "end", null);
            // Center mode: use center-based snapping (when ALT/OPTION is pressed)
            const snapPoint = floorplannerStore.getSnappingPositionForDrag(object.id, WallConnectionEnd, newEndPosition);
            if (snapPoint && newEndPosition.distanceTo(snapPoint) < snapTolerance) {
                newEndPosition.copy(snapPoint);
                snapped = true;
                edgeSnapApplied = true; // Mark as applied to skip angle snapping
            }
        }
    }

    // PRIORITY 2: 45-degree angle snapping (only if no edge snap was applied)
    // Skip angle snapping if we already snapped to a wall edge corner
    if (!edgeSnapApplied && !editorStore.isShiftPressed) {
        const deltaX = newEndPosition.x - object.start.x;
        const deltaY = newEndPosition.y - object.start.y;
        const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI); // Convert to degrees

        const currentDistance = newEndPosition.distanceTo(object.start);

        const normalizedAngle = (angle + 360) % 360;
        const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315, 360];
        let closestSnapAngle = snapAngles.reduce((prev, curr) => Math.abs(curr - normalizedAngle) < Math.abs(prev - normalizedAngle) ? curr : prev);

        const radianSnapAngle = closestSnapAngle * (Math.PI / 180);
        const snapEndX = object.start.x + currentDistance * Math.cos(radianSnapAngle);
        const snapEndY = object.start.y + currentDistance * Math.sin(radianSnapAngle);

        const snapDistance = Math.sqrt(Math.pow(newEndPosition.x - snapEndX, 2) + Math.pow(newEndPosition.y - snapEndY, 2));

        // If within snapping tolerance, snap to the nearest 45-degree angle
        if (snapDistance < angleSnapTolerance) {
            newEndPosition.x = snapEndX;
            newEndPosition.y = snapEndY;
            // Store the snapped angle for alignment filtering (alignment axes within 20° of wall angle are skipped)
            snappedWallAngle = closestSnapAngle === 360 ? 0 : closestSnapAngle;
        }
    }

    const intendedEndPosition = newEndPosition.clone();

    pendingPositions = pendingPositions ?? {
        start: pendingStartPosition.clone(),
        end: newEndPosition.clone(),
    };

    // Skip alignment check if we applied an edge snap (connection priority)
    // Note: angle snapping no longer skips alignment - instead, alignment axes within 20° of wall angle are filtered
    const skipAlignment = edgeSnapApplied;

    const alignmentResult = skipAlignment ? { applied: false, positions: null } : applyAlignmentAfterSnap({
        objectId: object.id,
        connectionEnd: WallConnectionEnd,
        alignmentKey: "end",
        intendedPoint: intendedEndPosition,
        suppressSnapping,
        existingPositions: pendingPositions,
        snappedWallAngle,
    });

    const finalPositions = alignmentResult.positions ?? pendingPositions;
    if (!finalPositions) {
        return snapped;
    }

    const positionsWithPivot = object.type === "wall"
        ? applyWallPivot(object as WallType, finalPositions, "end")
        : finalPositions;

    const transformedControlPoint = object.type === "wall" && object.lineForm === "arc" && object.controlPoint
        ? transformArcControlPoint(
            object.controlPoint,
            object.start,
            object.end,
            positionsWithPivot.start,
            positionsWithPivot.end,
        )
        : undefined;
    const shouldUpdateControlPoint = !!(
        transformedControlPoint &&
        object.controlPoint &&
        transformedControlPoint.distanceTo(object.controlPoint) > 1e-6
    );

    if (object.type === "wall") {
        // Force full geometry update for the dragged wall to ensure corners update correctly
        floorplannerStore.setObjectProperty(object.id, "cornerState", undefined);
    }
    floorplannerStore.updateObjectPosition(object.id, positionsWithPivot.start.clone(), positionsWithPivot.end.clone(), { bypassEdgeConstraints: true });
    if (shouldUpdateControlPoint && transformedControlPoint) {
        floorplannerStore.setObjectProperty(object.id, "controlPoint", transformedControlPoint);
    }
    if (object.type === "wall") {
        renderStore.composeWallShape(object.id, undefined, true);
        if (!renderStore.isDragging) {
            renderStore.trimShapes(object as WallType, undefined, undefined, { force: true });
            renderStore.generateOutlineSegments(object.id);
        } else {
            renderStore.generateOutlineSegments(object.id);
            renderStore.requestInvalidation(object.id);
        }
    }

    // Propagate movement to connected walls (maintain connections)
    // Calculate the movement delta from the original position to preserve edge-to-edge relationships
    const movementDelta = new THREE.Vector2().subVectors(positionsWithPivot.end, object.end);
    const connectedIds = floorplannerStore.getWallsConnectedAtPosition(object.id, WallConnectionEnd);
    connectedIds.forEach(id => {
        const connectedWall = floorplannerStore.findObjectId(id);
        if (connectedWall && connectedWall.type === 'wall') {
            let cStart = connectedWall.start.clone();
            let cEnd = connectedWall.end.clone();
            let modified = false;

            // Check based on edge connections/geometric proximity which end to update
            // For edge connections, apply the movement delta to preserve the edge-to-edge offset
            // This prevents the connected wall from jumping to center position

            // Explicit edge connection check
            const edgeConn = connectedWall.edgeConnections?.find(c => c.targetWallId === object.id && c.targetEndpoint === "end");
            if (edgeConn) {
                // Apply movement delta to preserve edge-to-edge relationship
                if (edgeConn.sourceEndpoint === "start") {
                    cStart.add(movementDelta);
                    modified = true;
                } else {
                    cEnd.add(movementDelta);
                    modified = true;
                }
            } else {
                // Fallback to geometric check for legacy/center connections
                // For center connections, copy the center position directly
                if (connectedWall.start.distanceTo(object.end) < 0.1) {
                    cStart.copy(positionsWithPivot.end);
                    modified = true;
                } else if (connectedWall.end.distanceTo(object.end) < 0.1) {
                    cEnd.copy(positionsWithPivot.end);
                    modified = true;
                }
            }

            if (modified) {
                floorplannerStore.updateObjectPosition(connectedWall.id, cStart, cEnd, { bypassEdgeConstraints: true });

                // Force full geometry update for connected wall to maintain correct connections during drag
                floorplannerStore.setObjectProperty(connectedWall.id, "cornerState", undefined);
                renderStore.composeWallShape(connectedWall.id, undefined, true);
                if (!renderStore.isDragging) {
                    renderStore.trimShapes(connectedWall, undefined, undefined, { force: true });
                    renderStore.generateOutlineSegments(connectedWall.id);
                } else {
                    renderStore.generateOutlineSegments(connectedWall.id);
                    renderStore.requestInvalidation(connectedWall.id);
                }
            }
        }
    });

    if (alignmentResult.applied) {
        snapped = true;
        newEndPosition.copy(positionsWithPivot.end);
        // Only set alignment locks on final drop, not during drag preview
        if (!suppressSnapping && dragState) {
            dragState.alignmentLocks = dragState.alignmentLocks || {};
            dragState.alignmentLocks.end = positionsWithPivot.end.clone();
        }
    }

    if (edgeSnapApplied) {
        // Only re-snap if there's no existing edge connection at this endpoint
        // This prevents edge-to-edge connections from being recalculated to center during drag
        const sourceWall = floorplannerStore.findObjectId(object.id) as WallType | undefined;
        const hasExistingEdgeConnection = sourceWall?.edgeConnections?.some(c => c.sourceEndpoint === "end");
        if (!hasExistingEdgeConnection) {
            floorplannerStore.snapToOtherObjectsEdgeBased(object.id, WallConnectionEnd, positionsWithPivot.end.clone(), true, { allowDuringDeferred: false });
        }
        const refreshedEnd = floorplannerStore.getEdgeSnappingPositionForDrag(object.id, "end", positionsWithPivot.end.clone());
        floorplannerStore.storeEdgeSnappingPreview(object.id, "end", refreshedEnd);
    }

    // Skip connection updates when snapping is suppressed
    if (!suppressSnapping) {
        // Update all walls connected at the start position
        floorplannerStore.getWallsConnectedAtPosition(object.id, WallConnectionStart).forEach(id => {
            floorplannerStore.objectNeedsUpdate(id);
        });
    }

    runInAction(() => {
        editorStore.setWhichEndToUpdate("end");
        if (object.type === "singleLine") {
            const lineLength = floorplannerStore.singleLineLength(object.id);
            editorStore.setSingleLineEditingLengthPanel(lineLength);
        } else if (object.type === "rulerLine") {
            const lineLength = floorplannerStore.rulerLineLength(object.id);
            editorStore.setRulerEditingLengthPanel(lineLength);
        } else if (object.type === "wall" && !suppressSnapping) {
            const wallLength = floorplannerStore.wallLength(object);
            editorStore.setWallEditingLengthPanel(wallLength);
        }
    });

    delta.set(newDelta.x, newDelta.y);
    return snapped;
}
